import { useEffect, useMemo, useRef, useState } from 'react';
import { promptTemplateApi } from '../../../api/promptTemplateApi';

const DRAFT_ID = 'new';

const apiError = (err, fallback) => {
  const message = err?.message || '';
  // The shared interceptor stringifies FastAPI's field-level 422 payload into
  // "[object Object]"; the checks in `save` cover those cases anyway.
  return !message || message.includes('[object Object]') ? fallback : message;
};

const byName = (a, b) => a.name.localeCompare(b.name);

const matchesFilter = (needle) => (item) => (
  item.name.toLowerCase().includes(needle) || item.prompt.toLowerCase().includes(needle)
);

const useTemplateList = (setError) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await promptTemplateApi.list();
        if (cancelled) return;
        setTemplates(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) setError(apiError(err, 'Failed to load prompt templates'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [setError]);

  return { templates, setTemplates, loading };
};

/**
 * The prompt template library: the list, the one template open in the editor
 * (or a new draft), and saving or deleting it. Leaving unsaved text asks first.
 */
export function usePromptTemplateLibrary(notify) {
  const [error, setError] = useState('');
  const { templates, setTemplates, loading } = useTemplateList(setError);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);

  const isDraft = selectedId === DRAFT_ID;
  const selected = templates.find((item) => item.id === selectedId) || null;
  const dirty = isDraft
    ? Boolean(name.trim() || prompt.trim())
    : Boolean(selected) && (name !== selected.name || prompt !== selected.prompt);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle ? templates.filter(matchesFilter(needle)) : templates;
  }, [templates, filter]);

  const fillEditor = (id, nextName, nextPrompt) => {
    setError('');
    setSelectedId(id);
    setName(nextName);
    setPrompt(nextPrompt);
  };
  const openTemplate = (template) => fillEditor(template.id, template.name, template.prompt);
  const openDraft = (draftName, draftPrompt) => {
    fillEditor(DRAFT_ID, draftName, draftPrompt);
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  // Losing typed text to a stray click is worse than one extra confirm.
  const leaveCurrent = () => !dirty || window.confirm('Discard your unsaved changes?');

  const select = (template) => {
    if (template.id === selectedId || !leaveCurrent()) return;
    openTemplate(template);
  };

  const startNew = () => {
    if (leaveCurrent()) openDraft('', '');
  };

  const duplicate = () => {
    if (selected && leaveCurrent()) openDraft(`${selected.name} copy`, selected.prompt);
  };

  const storeSaved = async (cleanName, cleanPrompt) => {
    if (isDraft) {
      const created = await promptTemplateApi.create({ name: cleanName, prompt: cleanPrompt });
      setTemplates((prev) => [...prev, created].sort(byName));
      return { template: created, message: `“${created.name}” is now in the dropdown.` };
    }
    const saved = await promptTemplateApi.update(selectedId, { name: cleanName, prompt: cleanPrompt });
    setTemplates((prev) => prev.map((item) => (item.id === saved.id ? saved : item)).sort(byName));
    return { template: saved, message: `“${saved.name}” updated.` };
  };

  const save = async (e) => {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanPrompt = prompt.trim();
    if (!cleanName) {
      setError('Name the template first.');
      return;
    }
    if (!cleanPrompt) {
      setError('Paste the prompt first.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const { template, message } = await storeSaved(cleanName, cleanPrompt);
      openTemplate(template);
      notify({ type: 'success', title: 'Saved', message });
    } catch (err) {
      setError(apiError(err, 'Failed to save the template'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete “${selected.name}”? Reports already generated from it stay as they are.`)) {
      return;
    }
    setError('');
    try {
      await promptTemplateApi.remove(selected.id);
      setTemplates((prev) => prev.filter((item) => item.id !== selected.id));
      fillEditor('', '', '');
      notify({ type: 'success', title: 'Deleted', message: 'The template is gone from the dropdown.' });
    } catch (err) {
      setError(apiError(err, 'Failed to delete the template'));
    }
  };

  return {
    templates, visible, loading, filter, setFilter,
    selectedId, isDraft, name, setName, prompt, setPrompt, nameRef, dirty, saving, error,
    select, startNew, duplicate, save, remove,
  };
}
